/**
 * HelpModal - Comprehensive Help & SOP Documentation
 * 
 * A clean, organized help center for the LGL Portal
 * Accessible via the "?" button in the header
 * 
 * Last Updated: December 2025
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ListenButton } from '../common/AudioGuide';
import { HelpVideoTourModal } from './HelpVideoTour';
import { listHelpTours } from './helpTours/registry';

// Section icons as simple text-based icons
const SECTION_ICONS = {
  'getting-started': '🚀',
  'navigation': '🧭',
  'projects': '📁',
  'features': '⚡',
  'workflows': '📋',
  'tips': '💡',
  'shortcuts': '⌨️',
  'faq': '❓',
  'video-tours': '🎬',
  'support': '📞'
};

const HelpModal = ({ isOpen, onClose, onStartTutorial, user }) => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTour, setActiveTour] = useState(null);
  const contentRef = useRef(null); // audio guide reads the active section from here

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sections = [
    { id: 'getting-started', label: 'Getting Started' },
    { id: 'navigation', label: 'Navigation' },
    { id: 'projects', label: 'Working with Projects' },
    { id: 'features', label: 'Key Features' },
    { id: 'workflows', label: 'Common Workflows' },
    { id: 'tips', label: 'Tips & Best Practices' },
    { id: 'shortcuts', label: 'Keyboard Shortcuts' },
    { id: 'faq', label: 'FAQ' },
    { id: 'video-tours', label: 'Video Tours' },
    { id: 'support', label: 'Contact Support' }
  ];

  const handleStartTutorial = () => {
    onClose();
    if (onStartTutorial) {
      onStartTutorial();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 10000,
          animation: 'fadeIn 0.2s ease'
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90vw',
          maxWidth: '1100px',
          height: '85vh',
          maxHeight: '800px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
          zIndex: 10001,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'modalSlideIn 0.3s ease'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid #e5e7eb',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #dc3545 100%)',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700' }}>
                📖 LGL Portal Help Center
              </h1>
              <p style={{ margin: '8px 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                Complete guide to using the LGL Portal effectively
              </p>
              <div style={{ marginTop: '10px' }}>
                <ListenButton
                  guideId={`help-center-${activeSection}`}
                  title="LGL Portal Help Center"
                  accent="#dc3545"
                  dark
                  getContent={() => contentRef.current}
                />
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                cursor: 'pointer',
                color: 'white',
                fontSize: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              ×
            </button>
          </div>

          {/* Search Bar */}
          <div style={{ marginTop: '16px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search help topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px 12px 44px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '0.95rem',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                color: '#333',
                outline: 'none'
              }}
            />
            <span style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '1.1rem',
              opacity: 0.5
            }}>
              🔍
            </span>
          </div>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Sidebar Navigation */}
          <nav style={{
            width: '240px',
            borderRight: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            overflowY: 'auto',
            flexShrink: 0
          }}>
            <div style={{ padding: '16px' }}>
              {/* Interactive Tutorial Button */}
              <button
                onClick={handleStartTutorial}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: '16px',
                  background: 'linear-gradient(135deg, #dc3545 0%, #b91c1c 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.3)';
                }}
              >
                ▶️ Start Interactive Tour
              </button>

              {/* Section Links */}
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    marginBottom: '4px',
                    background: activeSection === section.id ? '#fff' : 'transparent',
                    color: activeSection === section.id ? '#dc3545' : '#374151',
                    border: activeSection === section.id ? '1px solid #e5e7eb' : '1px solid transparent',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: activeSection === section.id ? '600' : '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    textAlign: 'left',
                    transition: 'all 0.15s ease',
                    boxShadow: activeSection === section.id ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (activeSection !== section.id) {
                      e.currentTarget.style.background = '#fff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeSection !== section.id) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }}>{SECTION_ICONS[section.id]}</span>
                  {section.label}
                </button>
              ))}
            </div>
          </nav>

          {/* Content Area */}
          <div ref={contentRef} style={{
            flex: 1,
            overflowY: 'auto',
            padding: '32px 40px'
          }}>
            <SectionContent 
              section={activeSection} 
              searchTerm={searchTerm}
              user={user}
              onOpenTour={setActiveTour}
            />
          </div>
        </div>

        <HelpVideoTourModal
          isOpen={!!activeTour}
          onClose={() => setActiveTour(null)}
          guideId={activeTour?.guideId}
          title={activeTour?.title}
          accent={activeTour?.accent}
          scenes={activeTour?.scenes}
        />

        {/* Footer */}
        <div style={{
          padding: '16px 32px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
            LGL Portal v2.0 • Last Updated: December 2025
          </span>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              background: '#374151',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#1f2937'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#374151'}
          >
            Close
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalSlideIn {
          from { 
            opacity: 0; 
            transform: translate(-50%, -48%);
          }
          to { 
            opacity: 1; 
            transform: translate(-50%, -50%);
          }
        }
      `}</style>
    </>
  );
};

// Section Content Component
const SectionContent = ({ section, searchTerm, user, onOpenTour }) => {
  const contentMap = {
    'getting-started': <GettingStartedSection />,
    'navigation': <NavigationSection />,
    'projects': <ProjectsSection />,
    'features': <FeaturesSection />,
    'workflows': <WorkflowsSection />,
    'tips': <TipsSection />,
    'shortcuts': <ShortcutsSection />,
    'faq': <FAQSection />,
    'video-tours': <VideoToursSection onOpenTour={onOpenTour} />,
    'support': <SupportSection user={user} />
  };

  return contentMap[section] || <GettingStartedSection />;
};

const VideoToursSection = ({ onOpenTour }) => {
  const tours = useMemo(() => listHelpTours(), []);
  const byGroup = useMemo(() => {
    const map = new Map();
    for (const t of tours) {
      const g = t.group || 'Other';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(t);
    }
    return [...map.entries()];
  }, [tours]);

  return (
    <>
      <SectionTitle
        icon="🎬"
        title="Video Tours"
        subtitle="Illustrated walkthroughs with Clara + Liam narration for help guides across the portal."
      />
      <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14 }}>
        {tours.length} tours available. You can also open any tour from a page&apos;s Help modal → Video Tour tab,
        or from Learning Center → Video Tutorials.
      </p>
      {byGroup.map(([group, items]) => (
        <div key={group} style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 12px', color: '#111827', fontSize: '1.05rem' }}>{group}</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            {items.map((tour) => (
              <button
                key={tour.guideId}
                type="button"
                onClick={() => onOpenTour?.(tour)}
                style={{
                  textAlign: 'left',
                  border: '1px solid #e5e7eb',
                  background: '#f9fafb',
                  borderRadius: 10,
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, color: '#111827' }}>{tour.title}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    {tour.scenes?.length || 0} chapters · {tour.guideId}
                  </div>
                </div>
                <span style={{ color: tour.accent || '#dc3545', fontWeight: 700, fontSize: 13 }}>▶ Play</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </>
  );
};

// ============================================
// SECTION COMPONENTS
// ============================================

const SectionTitle = ({ icon, title, subtitle }) => (
  <div style={{ marginBottom: '28px' }}>
    <h2 style={{ 
      margin: 0, 
      fontSize: '1.5rem', 
      fontWeight: '700', 
      color: '#111827',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      <span style={{ fontSize: '1.75rem' }}>{icon}</span>
      {title}
    </h2>
    {subtitle && (
      <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: '1rem' }}>
        {subtitle}
      </p>
    )}
  </div>
);

const InfoCard = ({ title, children, variant = 'default' }) => {
  const colors = {
    default: { bg: '#f9fafb', border: '#e5e7eb', accent: '#374151' },
    tip: { bg: '#ecfdf5', border: '#a7f3d0', accent: '#059669' },
    warning: { bg: '#fffbeb', border: '#fcd34d', accent: '#d97706' },
    info: { bg: '#eff6ff', border: '#93c5fd', accent: '#2563eb' }
  };
  const color = colors[variant];

  return (
    <div style={{
      backgroundColor: color.bg,
      border: `1px solid ${color.border}`,
      borderRadius: '12px',
      padding: '20px 24px',
      marginBottom: '16px'
    }}>
      {title && (
        <h4 style={{ margin: '0 0 12px', color: color.accent, fontSize: '1rem', fontWeight: '600' }}>
          {title}
        </h4>
      )}
      <div style={{ color: '#374151', fontSize: '0.95rem', lineHeight: '1.6' }}>
        {children}
      </div>
    </div>
  );
};

const StepList = ({ steps }) => (
  <ol style={{ 
    margin: '16px 0', 
    paddingLeft: '24px',
    lineHeight: '1.8'
  }}>
    {steps.map((step, index) => (
      <li key={index} style={{ 
        marginBottom: '12px', 
        color: '#374151',
        fontSize: '0.95rem'
      }}>
        <strong style={{ color: '#111827' }}>{step.title}</strong>
        {step.description && <span> — {step.description}</span>}
      </li>
    ))}
  </ol>
);

// ============================================
// GETTING STARTED SECTION
// ============================================
const GettingStartedSection = () => (
  <>
    <SectionTitle 
      icon="🚀" 
      title="Getting Started"
      subtitle="Welcome to the LGL Portal! Here's everything you need to know to get started."
    />

    <InfoCard title="What is LGL Portal?" variant="info">
      <p style={{ margin: 0 }}>
        LGL Portal is your central hub for <strong>project management</strong>, <strong>progress tracking</strong>, 
        <strong> estimation</strong>, and <strong>reporting</strong>. It's designed to streamline piping and 
        mechanical project workflows for the entire organization.
      </p>
    </InfoCard>

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Quick Start Guide</h3>
    
    <StepList steps={[
      { 
        title: 'Sign In', 
        description: 'Use your company Microsoft account to sign in automatically via Single Sign-On (SSO).' 
      },
      { 
        title: 'Select a Project', 
        description: 'Use the Project Dropdown in the header or navigate to see all your assigned projects.' 
      },
      { 
        title: 'Explore the Sidebar', 
        description: 'Once inside a project, the left sidebar shows all available tools organized by category.' 
      },
      { 
        title: 'Start Working', 
        description: 'Click any menu item to access estimation, tracking, materials, reports, and more.' 
      }
    ]} />

    <InfoCard title="💡 Pro Tip" variant="tip">
      <p style={{ margin: 0 }}>
        The system remembers your last viewed page per project. When you return to a project, 
        you'll be taken right back to where you left off!
      </p>
    </InfoCard>

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Your Role & Permissions</h3>
    <p style={{ color: '#374151', lineHeight: '1.7' }}>
      Your access level determines which features you can use. Common roles include:
    </p>
    <ul style={{ color: '#374151', lineHeight: '1.8', paddingLeft: '24px' }}>
      <li><strong>Viewer</strong> — Read-only access to view data and reports</li>
      <li><strong>Data Entry</strong> — Can input and edit data in assigned areas</li>
      <li><strong>Technical Coordinator (TC)</strong> — Full access to tracking and estimation tools</li>
      <li><strong>Project Manager (PM)</strong> — Can manage projects and team assignments</li>
      <li><strong>Admin</strong> — System administration and user management</li>
    </ul>
  </>
);

// ============================================
// NAVIGATION SECTION
// ============================================
const NavigationSection = () => (
  <>
    <SectionTitle 
      icon="🧭" 
      title="Navigation"
      subtitle="Learn how to navigate the LGL Portal efficiently."
    />

    <h3 style={{ marginBottom: '16px', color: '#111827' }}>Header Bar</h3>
    <p style={{ color: '#374151', lineHeight: '1.7', marginBottom: '16px' }}>
      The header bar at the top of every page contains your main navigation tools:
    </p>

    <InfoCard>
      <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '2' }}>
        <li><strong>LGL Logo</strong> — Click to return to the home page</li>
        <li><strong>Project Dropdown</strong> — Quick-switch between your projects with search</li>
        <li><strong>Home Button</strong> — Return to the main landing page</li>
        <li><strong>Admin Panel</strong> — Access system settings (admin users only)</li>
        <li><strong>? (Help)</strong> — Opens this help center</li>
        <li><strong>🖨️ (Print)</strong> — Print the current page</li>
        <li><strong>Sign Out</strong> — Log out of the system</li>
      </ul>
    </InfoCard>

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Project Dropdown</h3>
    <InfoCard variant="info">
      <p style={{ margin: 0 }}>
        The Project Dropdown in the header is your fastest way to switch projects. It shows the current 
        project name and allows you to:
      </p>
      <ul style={{ margin: '12px 0 0', paddingLeft: '20px', lineHeight: '1.8' }}>
        <li>Search projects by name or job number</li>
        <li>See all your assigned projects in a scrollable list</li>
        <li>Click "View All Projects →" to see the full project dashboard</li>
      </ul>
    </InfoCard>

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Project Sidebar</h3>
    <p style={{ color: '#374151', lineHeight: '1.7', marginBottom: '16px' }}>
      When inside a project, the left sidebar contains all available tools organized by category:
    </p>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
      {[
        { name: 'Estimation', desc: 'MTO, Material Index, Labour Rates, Rentals' },
        { name: 'Progress Tracking', desc: 'Piping, Mechanical, HVAC, Structural, etc.' },
        { name: 'Actuals', desc: 'Spent Hours, Earned Hours, Matbore %' },
        { name: 'Material Management', desc: 'Material Log, Tariffs, Purchasing' },
        { name: 'Reports', desc: 'Budget, Actuals, Earned, EVM, Client' },
        { name: 'Documentation', desc: 'Transmittals, Daily Reports, Photos' },
        { name: 'Procurement', desc: 'Contractor Selection, Criteria Weights' },
        { name: 'Training', desc: 'Keyboard Shortcuts, Mouse Gestures' }
      ].map((item, i) => (
        <div key={i} style={{
          padding: '14px 16px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>{item.name}</div>
          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{item.desc}</div>
        </div>
      ))}
    </div>

    <InfoCard title="💡 Sidebar Tips" variant="tip" style={{ marginTop: '24px' }}>
      <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
        <li>Drag the sidebar edge to resize it</li>
        <li>Click the collapse button to minimize to icons only</li>
        <li>Your sidebar width preference is saved automatically</li>
      </ul>
    </InfoCard>
  </>
);

// ============================================
// PROJECTS SECTION
// ============================================
const ProjectsSection = () => (
  <>
    <SectionTitle 
      icon="📁" 
      title="Working with Projects"
      subtitle="How to access, navigate, and manage your projects."
    />

    <h3 style={{ marginBottom: '16px', color: '#111827' }}>Accessing Projects</h3>
    <p style={{ color: '#374151', lineHeight: '1.7', marginBottom: '16px' }}>
      There are two ways to access your projects:
    </p>

    <InfoCard title="1. Project Dropdown (Recommended)">
      <p style={{ margin: 0 }}>
        Click the project dropdown in the header to see a searchable list of all your assigned projects. 
        Type to filter by project name or job number, then click to navigate directly.
      </p>
    </InfoCard>

    <InfoCard title="2. Projects Dashboard">
      <p style={{ margin: 0 }}>
        Click "View All Projects" in the dropdown footer to see the full projects dashboard with 
        project cards showing job details and discipline badges.
      </p>
    </InfoCard>

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Inside a Project</h3>
    <p style={{ color: '#374151', lineHeight: '1.7' }}>
      When you open a project, you'll see:
    </p>
    <ul style={{ color: '#374151', lineHeight: '1.8', paddingLeft: '24px' }}>
      <li><strong>Project Header</strong> — Shows job number, project name, and current page title</li>
      <li><strong>Sidebar Menu</strong> — All tools and features organized by category</li>
      <li><strong>Content Area</strong> — The main workspace for the selected tool</li>
    </ul>

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Creating New Projects</h3>
    <InfoCard variant="info">
      <p style={{ margin: 0 }}>
        <strong>Project Managers</strong> can create new projects by clicking the "+ New" button 
        that appears on the Projects Dashboard. Fill in the required details including job number, 
        project name, client, and active disciplines.
      </p>
    </InfoCard>
  </>
);

// ============================================
// FEATURES SECTION
// ============================================
const FeaturesSection = () => (
  <>
    <SectionTitle 
      icon="⚡" 
      title="Key Features"
      subtitle="Overview of the main features available in LGL Portal."
    />

    <h3 style={{ marginBottom: '16px', color: '#111827' }}>Progress Tracking (Piping Matrix)</h3>
    <InfoCard>
      <p style={{ marginTop: 0 }}>
        The flagship feature for tracking piping fabrication and installation progress. Features include:
      </p>
      <ul style={{ margin: '12px 0 0', paddingLeft: '20px', lineHeight: '1.8' }}>
        <li><strong>Excel-like Interface</strong> — Familiar spreadsheet experience with inline editing</li>
        <li><strong>Upload/Export</strong> — Import from Excel and export data anytime</li>
        <li><strong>Column Configuration</strong> — Show/hide columns, create custom UDF fields</li>
        <li><strong>Progress Trends</strong> — Click any line tag to see historical progress charts</li>
        <li><strong>Drag-Fill</strong> — Select cells and drag to fill values like Excel</li>
        <li><strong>Advanced Filtering</strong> — Filter any column with search, operators, or checkboxes</li>
        <li>
          <strong>🎬 New Employee Video Tour</strong> — Illustrated walkthrough with Clara + Liam narration.
          Open it from <em>Learning Center → Video Tutorials</em>, or from Piping tracker Help → <em>Video Tour</em>.
        </li>
      </ul>
    </InfoCard>

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Other Tracking Dashboards</h3>
    <div style={{ display: 'grid', gap: '12px' }}>
      {[
        { name: 'HVAC Tracking', desc: 'Track HVAC equipment installation progress' },
        { name: 'Rotating Equipment', desc: 'Monitor rotating equipment status' },
        { name: 'Ducts Tracking', desc: 'Track ductwork fabrication and installation' },
        { name: 'Vessels Tracking', desc: 'Pressure vessels and tanks progress' },
        { name: 'Structural Steel', desc: 'Steel structure fabrication tracking' },
        { name: 'Change Orders (CCR)', desc: 'Track change control requests and field work orders' }
      ].map((item, i) => (
        <div key={i} style={{
          padding: '12px 16px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{ 
            width: '8px', 
            height: '8px', 
            borderRadius: '50%', 
            backgroundColor: '#dc3545',
            flexShrink: 0
          }} />
          <div>
            <span style={{ fontWeight: '600', color: '#111827' }}>{item.name}</span>
            <span style={{ color: '#6b7280' }}> — {item.desc}</span>
          </div>
        </div>
      ))}
    </div>

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Reports & Analytics</h3>
    <InfoCard>
      <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
        <li><strong>Budget Report</strong> — Compare budgeted vs actual costs</li>
        <li><strong>Actuals Report</strong> — View actual hours spent by discipline</li>
        <li><strong>Earned Value Report</strong> — Track earned hours and progress</li>
        <li><strong>EVM Report</strong> — Earned Value Management with CPI/SPI metrics</li>
        <li><strong>Client Report</strong> — Generate reports for client review</li>
        <li><strong>HSE Report</strong> — Health, Safety & Environment metrics</li>
      </ul>
    </InfoCard>

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Material Management</h3>
    <InfoCard>
      <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
        <li><strong>Material Log</strong> — Track all project materials with status</li>
        <li><strong>Tariff Lookup</strong> — Search harmonized tariff codes</li>
        <li><strong>Purchasing List</strong> — Manage procurement items</li>
        <li><strong>Currency Exchange</strong> — Live CAD/USD rates for cost conversion</li>
      </ul>
    </InfoCard>
  </>
);

// ============================================
// WORKFLOWS SECTION
// ============================================
const WorkflowsSection = () => (
  <>
    <SectionTitle 
      icon="📋" 
      title="Common Workflows"
      subtitle="Step-by-step guides for common tasks."
    />

    <h3 style={{ marginBottom: '16px', color: '#111827' }}>Uploading Progress Data</h3>
    <StepList steps={[
      { title: 'Navigate to Tracker', description: 'Go to Progress Tracking → Piping (or other discipline)' },
      { title: 'Click Import', description: 'Click the "Import" button in the toolbar' },
      { title: 'Download Template', description: 'Click "Download Template" first to get the correct format with ~40 source-data columns' },
      { title: 'Select File', description: 'Drag and drop your Excel file or click to browse' },
      { title: 'Map Columns', description: 'Verify the column mapping preview' },
      { title: 'Confirm Upload', description: 'Click "Upload" to process the data' }
    ]} />
    <InfoCard variant="tip">
      <p style={{ marginTop: 0, fontWeight: 600 }}>Why does the template have fewer columns than the tracker?</p>
      <p style={{ margin: '8px 0 0' }}>
        The template only includes <strong>source data</strong> columns (~40) — line info, sizing, budget, WBS codes, etc.
        The database has ~68 total columns, but the following are excluded from the template because they are managed automatically:
      </p>
      <ul style={{ margin: '8px 0 0', paddingLeft: '20px', lineHeight: '1.8' }}>
        <li><strong>Progress columns</strong> (Shop Completion, Fit Up, Bolt/Weld, etc.) — start at 0 and are updated live in the tracker</li>
        <li><strong>Calculated columns</strong> (Earned Hours, Field Completion, Mat Bore, etc.) — computed automatically by the database</li>
        <li><strong>UDF columns</strong> (UDF01–UDF10) — managed through Column Config in the toolbar</li>
        <li><strong>System columns</strong> (ID, UUID, timestamps, Job Number) — set automatically</li>
      </ul>
    </InfoCard>

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Exporting Data</h3>
    <StepList steps={[
      { title: 'Open the Dashboard', description: 'Navigate to any tracking or log page' },
      { title: 'Click Export', description: 'Click the "Export" button in the toolbar' },
      { title: 'Choose Format', description: 'Select export type: Dashboard Only, All Columns, or Full Export' },
      { title: 'Download', description: 'The Excel file will download automatically' }
    ]} />

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Editing Data Inline</h3>
    <InfoCard variant="tip">
      <p style={{ marginTop: 0 }}>
        Most dashboards support inline editing:
      </p>
      <ul style={{ margin: '12px 0 0', paddingLeft: '20px', lineHeight: '1.8' }}>
        <li><strong>Click</strong> any editable cell to start editing</li>
        <li><strong>Tab</strong> to move to the next cell</li>
        <li><strong>Enter</strong> to save and move down</li>
        <li><strong>Escape</strong> to cancel changes</li>
        <li>Changes are <strong>auto-saved</strong> when you leave the cell</li>
      </ul>
    </InfoCard>

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Viewing Progress Trends</h3>
    <StepList steps={[
      { title: 'Open Piping Tracker', description: 'Go to Progress Tracking → Piping' },
      { title: 'Click Line Tag', description: 'Click any line tag (blue link) in the first column' },
      { title: 'View Chart', description: 'A modal shows the progress history over time' },
      { title: 'Analyze', description: 'See fabrication, painting, and installation trends' }
    ]} />

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Configuring Columns</h3>
    <StepList steps={[
      { title: 'Click Column Config', description: 'Find the ⚙️ button in the toolbar' },
      { title: 'Toggle Visibility', description: 'Check/uncheck columns to show or hide them' },
      { title: 'Create UDFs', description: 'Add custom User Defined Fields if needed' },
      { title: 'Save', description: 'Your preferences are saved automatically per project' }
    ]} />
  </>
);

// ============================================
// TIPS SECTION
// ============================================
const TipsSection = () => (
  <>
    <SectionTitle 
      icon="💡" 
      title="Tips & Best Practices"
      subtitle="Get the most out of LGL Portal with these tips."
    />

    <InfoCard title="🎯 Efficiency Tips" variant="tip">
      <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
        <li>Use the <strong>Project Dropdown</strong> for quick project switching instead of navigating back</li>
        <li>Press <strong>Ctrl+F</strong> (or Cmd+F) to use browser search on any page</li>
        <li>The system <strong>auto-saves</strong> your work — no need to click save buttons</li>
        <li><strong>Collapse the sidebar</strong> for more screen space when working with large tables</li>
        <li>Use column <strong>filters</strong> to find specific data quickly</li>
      </ul>
    </InfoCard>

    <InfoCard title="📊 Data Management" variant="info">
      <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
        <li><strong>Export regularly</strong> to keep backups of your data</li>
        <li>Use the <strong>template download</strong> feature for correctly formatted uploads</li>
        <li><strong>Check column mappings</strong> carefully before confirming uploads</li>
        <li>Use <strong>filters</strong> before exporting to get exactly the data you need</li>
      </ul>
    </InfoCard>

    <InfoCard title="⚠️ Common Mistakes to Avoid" variant="warning">
      <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
        <li>Don't upload files with <strong>merged cells</strong> — they cause import errors</li>
        <li>Ensure <strong>column headers match</strong> the expected format</li>
        <li>Don't close the browser <strong>during file uploads</strong> — wait for completion</li>
        <li>Check that <strong>number formats</strong> are consistent (no text in number columns)</li>
      </ul>
    </InfoCard>

    <InfoCard title="🔒 Security Best Practices">
      <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
        <li>Always <strong>sign out</strong> when using shared computers</li>
        <li>Don't share your login credentials with others</li>
        <li>Report any suspicious activity to your administrator</li>
        <li>Be careful when exporting sensitive project data</li>
      </ul>
    </InfoCard>
  </>
);

// ============================================
// SHORTCUTS SECTION
// ============================================
const ShortcutsSection = () => (
  <>
    <SectionTitle 
      icon="⌨️" 
      title="Keyboard Shortcuts"
      subtitle="Speed up your workflow with these keyboard shortcuts."
    />

    <h3 style={{ marginBottom: '16px', color: '#111827' }}>Navigation</h3>
    <ShortcutTable shortcuts={[
      { keys: ['Esc'], description: 'Close modal / Cancel editing' },
      { keys: ['Ctrl', 'F'], description: 'Open browser search' },
      { keys: ['Ctrl', 'P'], description: 'Print current page' }
    ]} />

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Table Editing</h3>
    <ShortcutTable shortcuts={[
      { keys: ['Tab'], description: 'Move to next cell' },
      { keys: ['Shift', 'Tab'], description: 'Move to previous cell' },
      { keys: ['Enter'], description: 'Save and move down' },
      { keys: ['Escape'], description: 'Cancel editing' },
      { keys: ['↑', '↓', '←', '→'], description: 'Navigate between cells' }
    ]} />

    <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#111827' }}>Selection (Piping Matrix)</h3>
    <ShortcutTable shortcuts={[
      { keys: ['Click', 'Drag'], description: 'Select range of cells' },
      { keys: ['Shift', 'Click'], description: 'Extend selection' },
      { keys: ['Ctrl', 'Click'], description: 'Add to selection (multi-select)' },
      { keys: ['Ctrl', 'A'], description: 'Select all visible rows' }
    ]} />

    <InfoCard title="💡 Mouse Gestures" variant="tip" style={{ marginTop: '24px' }}>
      <p style={{ margin: 0 }}>
        In the Piping Matrix, you can <strong>drag-fill</strong> values like Excel:
      </p>
      <ol style={{ margin: '12px 0 0', paddingLeft: '20px', lineHeight: '1.8' }}>
        <li>Select a cell or range with a value</li>
        <li>Grab the small square handle at the bottom-right corner</li>
        <li>Drag down to fill the value into adjacent cells</li>
      </ol>
    </InfoCard>
  </>
);

const ShortcutTable = ({ shortcuts }) => (
  <div style={{ 
    backgroundColor: '#f9fafb', 
    borderRadius: '8px', 
    overflow: 'hidden',
    border: '1px solid #e5e7eb'
  }}>
    {shortcuts.map((shortcut, index) => (
      <div 
        key={index}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: index < shortcuts.length - 1 ? '1px solid #e5e7eb' : 'none'
        }}
      >
        <div style={{ display: 'flex', gap: '6px', minWidth: '160px' }}>
          {shortcut.keys.map((key, i) => (
            <React.Fragment key={i}>
              <kbd style={{
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}>
                {key}
              </kbd>
              {i < shortcut.keys.length - 1 && (
                <span style={{ color: '#9ca3af', alignSelf: 'center' }}>+</span>
              )}
            </React.Fragment>
          ))}
        </div>
        <span style={{ color: '#374151' }}>{shortcut.description}</span>
      </div>
    ))}
  </div>
);

// ============================================
// FAQ SECTION
// ============================================
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: 'Why can\'t I see certain projects?',
      a: 'You only see projects you\'ve been assigned to. Contact your Project Manager or Admin to be added to a project.'
    },
    {
      q: 'My upload failed. What should I do?',
      a: 'Check that your Excel file has no merged cells, headers match expected format, and data types are correct (numbers in number columns). Try downloading the template first for the correct format.'
    },
    {
      q: 'How do I add custom columns to the tracker?',
      a: 'Click the ⚙️ Column Config button, then use the "Add UDF" option to create User Defined Fields. You can add up to 16 custom columns.'
    },
    {
      q: 'Can I undo changes?',
      a: 'Individual cell changes are auto-saved and cannot be undone. For bulk changes, consider exporting a backup before making major edits.'
    },
    {
      q: 'Why is the page loading slowly?',
      a: 'Large datasets may take a moment to load. Try using column filters to reduce visible data, or collapse columns you don\'t need.'
    },
    {
      q: 'How do I print a report?',
      a: 'Click the 🖨️ button in the header or press Ctrl+P. The page will be formatted for printing automatically.'
    },
    {
      q: 'What browsers are supported?',
      a: 'LGL Portal works best on Chrome, Edge, and Firefox. Safari is also supported. Internet Explorer is not supported.'
    },
    {
      q: 'How do I change my password?',
      a: 'LGL Portal uses Microsoft Single Sign-On. To change your password, update it through your Microsoft/company account settings.'
    }
  ];

  return (
    <>
      <SectionTitle 
        icon="❓" 
        title="Frequently Asked Questions"
        subtitle="Answers to common questions about using LGL Portal."
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {faqs.map((faq, index) => (
          <div
            key={index}
            style={{
              backgroundColor: openIndex === index ? '#f9fafb' : 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'hidden',
              transition: 'background-color 0.2s'
            }}
          >
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left'
              }}
            >
              <span style={{ fontWeight: '600', color: '#111827', fontSize: '0.95rem' }}>
                {faq.q}
              </span>
              <span style={{
                transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                color: '#6b7280',
                fontSize: '0.8rem'
              }}>
                ▼
              </span>
            </button>
            {openIndex === index && (
              <div style={{
                padding: '0 20px 16px',
                color: '#374151',
                lineHeight: '1.6',
                fontSize: '0.95rem'
              }}>
                {faq.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

// ============================================
// SUPPORT SECTION
// ============================================
const SupportSection = ({ user }) => (
  <>
    <SectionTitle 
      icon="📞" 
      title="Contact Support"
      subtitle="Need help? Here's how to reach us."
    />

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
      <InfoCard title="📧 Email Support">
        <p style={{ margin: '0 0 12px' }}>
          For general inquiries and non-urgent issues:
        </p>
        <a 
          href="mailto:support@lglgroup.com"
          style={{ 
            color: '#dc3545', 
            fontWeight: '600',
            textDecoration: 'none'
          }}
        >
          support@lglgroup.com
        </a>
      </InfoCard>

      <InfoCard title="🐛 Report a Bug">
        <p style={{ margin: '0 0 12px' }}>
          Found a bug? Please include:
        </p>
        <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.6', fontSize: '0.9rem' }}>
          <li>What you were trying to do</li>
          <li>What happened instead</li>
          <li>Steps to reproduce</li>
          <li>Screenshots if possible</li>
        </ul>
      </InfoCard>

      <InfoCard title="💬 Feature Requests">
        <p style={{ margin: 0 }}>
          Have an idea to improve LGL Portal? We'd love to hear it! 
          Email your suggestions to the development team.
        </p>
      </InfoCard>

      <InfoCard title="📚 Training">
        <p style={{ margin: 0 }}>
          Need hands-on training? Contact your Project Manager to arrange 
          a training session with the LGL Portal team.
        </p>
      </InfoCard>
    </div>

    <div style={{ 
      marginTop: '32px',
      padding: '24px',
      backgroundColor: '#1a1a1a',
      borderRadius: '12px',
      color: 'white',
      textAlign: 'center'
    }}>
      <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>
        You're signed in as
      </h3>
      <p style={{ margin: '0 0 4px', fontSize: '1.25rem', fontWeight: '600' }}>
        {user?.display_name || 'User'}
      </p>
      <p style={{ margin: 0, opacity: 0.7, fontSize: '0.9rem' }}>
        {user?.email || ''}
      </p>
    </div>
  </>
);

export default HelpModal;
